import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Button, Modal, Form, Select, Input, Switch, Popconfirm, Space, Typography, message, DatePicker, InputNumber, Badge } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, EyeOutlined, PushpinOutlined } from '@ant-design/icons';
import api from '../api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const typeOptions = [
  { value: 'normal', label: '普通', color: 'default' },
  { value: 'important', label: '重要', color: 'orange' },
  { value: 'urgent', label: '紧急', color: 'red' },
];

const typeLabels = {
  normal: { label: '普通', color: 'default' },
  important: { label: '重要', color: 'orange' },
  urgent: { label: '紧急', color: 'red' },
};

const targetOptions = [
  { value: 'all', label: '全部用户' },
  { value: 'specific', label: '指定用户' },
];

function AnnouncementPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadAnnouncements = async (p = 1) => {
    setLoading(true);
    try {
      const { list, total: t } = await api.get('/admin/announcements', { params: { page: p, limit: 20 } });
      setAnnouncements(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      message.error('加载公告列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAnnouncements(); }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        start_time: values.time_range?.[0]?.format('YYYY-MM-DD HH:mm:ss') || null,
        end_time: values.time_range?.[1]?.format('YYYY-MM-DD HH:mm:ss') || null,
        time_range: undefined,
        target_users: values.target_users || null,
      };
      await api.post('/admin/announcements', payload);
      message.success('公告发布成功');
      setCreateModal(false);
      form.resetFields();
      loadAnnouncements(page);
    } catch (e) {
      if (e.errorFields) return;
      message.error('发布失败');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      ...record,
      time_range: record.start_time && record.end_time
        ? [dayjs(record.start_time), dayjs(record.end_time)]
        : null,
    });
    setEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      const payload = {
        ...values,
        start_time: values.time_range?.[0]?.format('YYYY-MM-DD HH:mm:ss') || null,
        end_time: values.time_range?.[1]?.format('YYYY-MM-DD HH:mm:ss') || null,
        time_range: undefined,
        target_users: values.target_users || null,
      };
      await api.put(`/admin/announcements/${editingRecord.id}`, payload);
      message.success('更新成功');
      setEditModal(false);
      loadAnnouncements(page);
    } catch (e) {
      if (e.errorFields) return;
      message.error('更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/announcements/${id}`);
      message.success('公告已删除');
      loadAnnouncements(page);
    } catch (e) {
      message.error('删除失败');
    }
  };

  const handleToggleStatus = async (id, status) => {
    try {
      await api.put(`/admin/announcements/${id}`, { status: status ? 1 : 0 });
      message.success(status ? '已启用' : '已禁用');
      loadAnnouncements(page);
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleToggleTop = async (id, isTop) => {
    try {
      await api.put(`/admin/announcements/${id}`, { is_top: isTop ? 1 : 0 });
      message.success(isTop ? '已置顶' : '已取消置顶');
      loadAnnouncements(page);
    } catch (e) {
      message.error('操作失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '标题', dataIndex: 'title', width: 250, ellipsis: true },
    {
      title: '类型', dataIndex: 'type', width: 100,
      render: (v) => <Tag color={typeLabels[v]?.color}>{typeLabels[v]?.label || v}</Tag>,
    },
    {
      title: '目标', dataIndex: 'target', width: 120,
      render: (v) => <Tag color={v === 'all' ? 'blue' : 'purple'}>{v === 'all' ? '全部用户' : '指定用户'}</Tag>,
    },
    {
      title: '置顶', dataIndex: 'is_top', width: 80,
      render: (v, r) => (
        <Switch
          checked={v === 1}
          onChange={(checked) => handleToggleTop(r.id, checked)}
          checkedChildren={<PushpinOutlined />}
          unCheckedChildren={<PushpinOutlined />}
          size="small"
        />
      ),
    },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (v, r) => (
        <Switch
          checked={v === 1}
          onChange={(checked) => handleToggleStatus(r.id, checked)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          size="small"
        />
      ),
    },
    {
      title: '展示时间', width: 280,
      render: (_, r) => (
        <span>
          {r.start_time ? new Date(r.start_time).toLocaleString() : '-'} ~<br />
          {r.end_time ? new Date(r.end_time).toLocaleString() : '-'}
        </span>
      ),
    },
    { title: '浏览量', dataIndex: 'view_count', width: 100, render: (v) => <Badge count={v || 0} showZero style={{ backgroundColor: '#52c41a' }} /> },
    { title: '创建时间', dataIndex: 'created_at', width: 180, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', width: 180,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该公告?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>公告管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ type: 'normal', target: 'all' }); setCreateModal(true); }} style={{ background: '#07C160' }}>
          发布公告
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={announcements}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            onChange: loadAnnouncements,
            showSizeChanger: false,
          }}
          size="small"
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 创建公告 Modal */}
      <Modal title="发布公告" open={createModal} onOk={handleCreate} onCancel={() => setCreateModal(false)} width={650}>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="公告标题" rules={[{ required: true, message: '请输入公告标题' }]}>
            <Input placeholder="输入公告标题" maxLength={100} />
          </Form.Item>
          <Form.Item name="content" label="公告内容" rules={[{ required: true, message: '请输入公告内容' }]}>
            <TextArea rows={5} placeholder="输入公告内容" maxLength={2000} showCount />
          </Form.Item>
          <Form.Item name="type" label="公告类型" rules={[{ required: true }]} initialValue="normal">
            <Select placeholder="选择公告类型">
              {typeOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>
                  <Tag color={o.color}>{o.label}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="target" label="目标用户" rules={[{ required: true }]} initialValue="all">
            <Select placeholder="选择目标用户">
              {targetOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.target !== cur.target}>
            {({ getFieldValue }) =>
              getFieldValue('target') === 'specific' ? (
                <Form.Item name="target_users" label="指定用户ID">
                  <Input placeholder="输入用户ID，多个用逗号分隔" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="time_range" label="展示时间">
            <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑公告 Modal */}
      <Modal title="编辑公告" open={editModal} onOk={handleUpdate} onCancel={() => setEditModal(false)} width={650}>
        <Form form={editForm} layout="vertical">
          <Form.Item name="title" label="公告标题" rules={[{ required: true, message: '请输入公告标题' }]}>
            <Input placeholder="输入公告标题" maxLength={100} />
          </Form.Item>
          <Form.Item name="content" label="公告内容" rules={[{ required: true, message: '请输入公告内容' }]}>
            <TextArea rows={5} placeholder="输入公告内容" maxLength={2000} showCount />
          </Form.Item>
          <Form.Item name="type" label="公告类型" rules={[{ required: true }]}>
            <Select placeholder="选择公告类型">
              {typeOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>
                  <Tag color={o.color}>{o.label}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="target" label="目标用户" rules={[{ required: true }]}>
            <Select placeholder="选择目标用户">
              {targetOptions.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.target !== cur.target}>
            {({ getFieldValue }) =>
              getFieldValue('target') === 'specific' ? (
                <Form.Item name="target_users" label="指定用户ID">
                  <Input placeholder="输入用户ID，多个用逗号分隔" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="time_range" label="展示时间">
            <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default AnnouncementPage;
